import { type ComputedRef, computed, type Ref, ref, reactive } from 'vue'
import type { KnobObject } from '../../Knob'
import { type AABB, getAABBPoints, SAT, range } from 'math'
import { useWindowEvents } from '@/utils/hooks/useWindowEvents'
import { useMousePosition } from '@/utils/hooks/useMousePosition'
import { KNOB_SIZE } from '../../constants'
import { useEffect } from '@/utils/reactive/useEffect'

export type UseKnobsProps = {
  knobs: KnobObject[]
  bounds: Ref<AABB>;
  onChange?: (values: number[]) => void | Promise<void>;
  onKnobChange?: (knob: KnobObject, oldValue: number, nextValue: number) => void | Promise<void>;
  count: number;
}

export type UseKnobsState = {
  knobs: KnobObject[]
  numBoundUpdates: number
  mouseIsDown: boolean
  ready: boolean
}

export type UseKnobs = {
  knobs: ComputedRef<KnobObject[]>
  values: ComputedRef<number[]>
  knobsInfo: {
    refs: Ref[]
  };
  version: Ref<number>;

  push: (knob: KnobObject | KnobObject[]) => void;
  destroy: () => void;
  clearKnobs: () => void;
  isReady: () => boolean;
}

export const useKnobs = (props: UseKnobsProps): UseKnobs => {
  const bounds = props.bounds
  const events = useWindowEvents()
  const mouse = useMousePosition();
  const version = ref<number>(0);

  const upgrade = () => {
    version.value = version.value + 1;
  }

  const knobsInfo = {
    refs: range(props.count || 1).map(() => ref())
  }

  const state = reactive<UseKnobsState>({
    knobs: props.knobs,
    numBoundUpdates: 0,
    mouseIsDown: false,
    ready: false,
  }) as UseKnobsState;

  const push = (knob: KnobObject | KnobObject[]) => {
    upgrade();

    if (Array.isArray(knob)) {
      state.knobs = knob;
    } else {
      state.knobs = [...state.knobs, knob];
    }
  }


  useEffect((ctx) => {
    if (state.numBoundUpdates > 3) {
      ctx.pause();
      return;
    };
    state.knobs.forEach((knob) => knob.updatePositionRange(bounds.value))
    state.numBoundUpdates = state.numBoundUpdates + 1
  }, [bounds], true, false, true);

  events.addEventListener('mouseup', () => {
    state.knobs.forEach((knob) => knob.setDragging(false))
  })

  const values = computed(() => {
    return state.knobs.map((it) => it.getValue())
  })

  const knobs = computed<KnobObject[]>(() => state.knobs as any)

  useEffect(() => {
    const knob = state.knobs.find((k) => k.dragging === true)
    if (!knob) return
    const mousePos = mouse.position.value
    knob.onMouseMove(mousePos, mouse.delta.value)
  }, [mouse.position], true, false, true);

  if (props.onChange) {
    useEffect(() => {
      if (props.onChange) {
        props.onChange(values.value)
      }
    }, [values], true, false, true);
  }

  const isReady = () => {
    if (bounds.value.max.x <= 1) return false;
    if (state.knobs.length < props.count) return false;
    if (state.ready === true) return true;
    const ready = state.knobs.filter(it => it.isReady() === true).length >= state.knobs.length;
    state.ready = ready;
    return ready;
  }


  const resolveIntersections = (knob: KnobObject) => {
    const knobz = knobs.value;

    const boundA = knob.getBoundingBox();
    const pointsA = getAABBPoints(boundA);
    if (knob.range.knobBehaviour?.pushSiblings) {
      for (let i = 0; i < knobz.length; i++) {
        const b = knobz[i];
        if (b.uid === knob.uid) continue
        const boundB = b.getBoundingBox();
        const pointsB = getAABBPoints(boundB);

        const inter = SAT(pointsA, pointsB);

        if (inter) {
          const depth = inter.depth * inter.normal.x;
          b.pos.x += 0.5 * depth;
          knob.pos.x -= 0.5 * depth;
        }
      }
    } else {
      for (let i = 0; i < knobz.length; i++) {
        const b = knobz[i]
        if (b.uid === knob.uid) continue
        const boundB = b.getBoundingBox();
        if (b.index < knob.index) {
          knob.pos.x = Math.max(knob.pos.x, boundB.max.x);
          knob.clamp();
        }
        if (b.index > knob.index) {
          knob.pos.x = Math.min(knob.pos.x, b.pos.x - KNOB_SIZE);
          knob.clamp();
        }
      }
    }
  }

  const onValueChange = async (knob: KnobObject, oldValue: number, nextValue: number) => {
    if (!isReady()) return;
    if (state.knobs.length > 1) {
      resolveIntersections(knob);
    }
    if (props.onKnobChange) {
      props.onKnobChange(knob, oldValue, nextValue);
    }
  }


  useEffect(() => {
    state.knobs.forEach((kn) => kn.addValueListener(onValueChange))
  }, [() => state.knobs.length], true, false, true);

  const destroy = () => {
    state.knobs = [];
    state.numBoundUpdates = 0;
    state.ready = false;
    knobsInfo.refs = [];
  }

  const clearKnobs = () => {
    state.knobs = [];
  }

  return { knobs: knobs, values, knobsInfo, push, version, destroy, isReady, clearKnobs }
}
